import React, { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

export default function Reviews() {
  const { t } = useLanguage();
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setSelected(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
  }, [embla]);

  const items = t.reviews.items;

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 md:py-32 border-t border-[#E5E0D8]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-4">{t.reviews.eyebrow}</div>
            <h2
              data-testid="reviews-title"
              className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1A1A1A]"
            >
              {t.reviews.title}
            </h2>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end items-center gap-3">
            <button
              data-testid="reviews-prev"
              onClick={() => embla && embla.scrollPrev()}
              className="h-12 w-12 border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] flex items-center justify-center transition-colors"
              aria-label="prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              data-testid="reviews-next"
              onClick={() => embla && embla.scrollNext()}
              className="h-12 w-12 border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] flex items-center justify-center transition-colors"
              aria-label="next"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={emblaRef} data-testid="reviews-carousel">
          <div className="flex">
            {items.map((rev, idx) => (
              <div
                key={idx}
                data-testid={`review-slide-${idx}`}
                className="min-w-0 flex-[0_0_100%] md:flex-[0_0_50%] lg:flex-[0_0_40%] pr-8"
              >
                <div className="border border-[#E5E0D8] bg-white p-8 md:p-10 h-full">
                  <Quote className="h-8 w-8 text-[#8B1E0F] mb-6" />
                  <p className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] leading-relaxed italic">
                    "{rev.text}"
                  </p>
                  <div className="mt-8 pt-6 border-t border-[#E5E0D8] flex items-center justify-between">
                    <div className="text-sm font-semibold tracking-wide text-[#1A1A1A]">
                      {rev.author}
                    </div>
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className="h-4 w-4 fill-[#8B1E0F] text-[#8B1E0F]"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="mt-8 flex gap-2">
          {items.map((_, i) => (
            <button
              key={i}
              data-testid={`review-dot-${i}`}
              onClick={() => embla && embla.scrollTo(i)}
              className={`h-1 transition-all ${
                selected === i ? "w-10 bg-[#8B1E0F]" : "w-5 bg-[#E5E0D8]"
              }`}
              aria-label={`go to review ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
